import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { resumeService, matcherService, coverLetterService, trackActivity, getBlobErrorMessage, fromServerFormat, LAST_TEMPLATE_KEY } from '../services/api';

import { ResumeRequest, PersonalInfo, Experience, Education, MatchedCV, JDAnalysis as JDAnalysisType, MatchScore } from '../types';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import toast from 'react-hot-toast';
import MatchScoreComponent from '../components/MatchScore';
import JDAnalysisComponent from '../components/JDAnalysis';
import RichTextEditor from '../components/RichTextEditor';
import { TemplatesGridSkeleton, FormSkeleton } from '../components/Skeleton';
import { ArrowLeft, Plus, X, Check, Download, Eye, FileText, ArrowRight, Search, Lock, ArrowUp, ArrowDown } from 'lucide-react';

interface PreviewData {
  template: string;
  html: string;
  loading: boolean;
}

interface TemplateItem {
  id: string;
  name: string;
  free: boolean;
}

interface TemplatePage {
  templates: TemplateItem[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

const emptyPersonalInfo = (): PersonalInfo => ({
  fullName: '',
  email: '',
  phone: '',
  address: '',
  linkedin: '',
  website: '',
  photo: '',
});

const emptyFormData = (): ResumeRequest => ({
  templateId: '',
  personalInfo: emptyPersonalInfo(),
  summary: '',
  experience: [],
  education: [],
  skills: [],
});

const ManualCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro, loading: subLoading } = useSubscription();
  const profileFullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const profileEmail = user?.email || '';
  const profilePhoto = user?.avatarUrl || '';
  const [currentStep, setCurrentStep] = useState<'form' | 'templates'>('form');
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<ResumeRequest>(emptyFormData);
  const [allTemplates, setAllTemplates] = useState<TemplateItem[]>([]);
  const [templatePage, setTemplatePage] = useState<TemplatePage | null>(null);
  const [previews, setPreviews] = useState<PreviewData[]>([]);
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewModal, setPreviewModal] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [jdUrl, setJdUrl] = useState('');
  const [jdText, setJdText] = useState('');
  const [useTextMatch, setUseTextMatch] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [matchData, setMatchData] = useState<{ jdUrl: string; matchedCV: MatchedCV; jdAnalysis: JDAnalysisType; matchScore: MatchScore } | null>(null);
  const [skillInput, setSkillInput] = useState('');

  useEffect(() => {
    if (user?.id) {
      fetchProfile(user.id);
    } else {
      setFormData({ ...emptyFormData(), personalInfo: { ...emptyPersonalInfo(), photo: profilePhoto } });
      setIsLoading(false);
    }
  }, [user?.id]);

  const fetchProfile = async (userId: string) => {
    try {
      const response = await resumeService.getProfile(userId);
      const converted = fromServerFormat(response.data as unknown as Record<string, unknown>);
      setFormData({
        ...converted,
        personalInfo: {
          ...converted.personalInfo,
          fullName: profileFullName || converted.personalInfo.fullName,
          email: profileEmail || converted.personalInfo.email,
          photo: converted.personalInfo.photo || profilePhoto,
        },
      });
    } catch {
      setFormData({ ...emptyFormData(), personalInfo: { ...emptyPersonalInfo(), photo: profilePhoto } });
    } finally {
      setIsLoading(false);
    }
  };

  const updatePersonalInfo = (field: keyof PersonalInfo, value: string) => {
    setFormData(prev => ({ ...prev, personalInfo: { ...prev.personalInfo, [field]: value } }));
  };

  const updateSummary = (value: string) => {
    setFormData(prev => ({ ...prev, summary: value }));
  };

  const updateExperience = (index: number, field: keyof Experience, value: string | boolean) => {
    setFormData(prev => {
      const updated = [...prev.experience];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'current' && value === true) {
        updated[index].endDate = '';
      }
      return { ...prev, experience: updated };
    });
  };

  const addExperience = () => {
    setFormData(prev => ({
      ...prev,
      experience: [{ company: '', role: '', startDate: '', endDate: '', current: false, description: '' }, ...prev.experience],
    }));
  };

  const removeExperience = (index: number) => {
    setFormData(prev => ({ ...prev, experience: prev.experience.filter((_, i) => i !== index) }));
  };

  const moveExperience = (index: number, direction: -1 | 1) => {
    setFormData(prev => {
      const target = index + direction;
      if (target < 0 || target >= prev.experience.length) return prev;
      const updated = [...prev.experience];
      [updated[index], updated[target]] = [updated[target], updated[index]];
      return { ...prev, experience: updated };
    });
  };

  const updateEducation = (index: number, field: keyof Education, value: string | boolean) => {
    setFormData(prev => {
      const updated = [...prev.education];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'current' && value === true) {
        updated[index].endDate = '';
      }
      return { ...prev, education: updated };
    });
  };

  const addEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [{ institution: '', degree: '', fieldOfStudy: '', year: '', startDate: '', endDate: '', current: false, gpa: '' }, ...prev.education],
    }));
  };

  const removeEducation = (index: number) => {
    setFormData(prev => ({ ...prev, education: prev.education.filter((_, i) => i !== index) }));
  };

  const moveEducation = (index: number, direction: -1 | 1) => {
    setFormData(prev => {
      const target = index + direction;
      if (target < 0 || target >= prev.education.length) return prev;
      const updated = [...prev.education];
      [updated[index], updated[target]] = [updated[target], updated[index]];
      return { ...prev, education: updated };
    });
  };

  const addSkill = () => {
    const trimmed = skillInput.trim();
    if (!trimmed) return;
    if (formData.skills.includes(trimmed)) {
      toast.error('Skill already added', { id: 'skill-duplicate' });
      return;
    }
    setFormData(prev => ({ ...prev, skills: [...prev.skills, trimmed] }));
    setSkillInput('');
  };

  const removeSkill = (index: number) => {
    setFormData(prev => ({ ...prev, skills: prev.skills.filter((_, i) => i !== index) }));
  };

  const handleSkillKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  const handleContinue = async () => {
    const resolvedName = profileFullName || formData.personalInfo.fullName;
    const resolvedEmail = profileEmail || formData.personalInfo.email;
    if (!resolvedName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!resolvedEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    let existingTemplateId = '';
    if (user?.id && !formData.templateId) {
      try {
        const existing = await resumeService.getProfile(user.id);
        existingTemplateId = existing.data?.templateId || '';
      } catch {
        // No saved profile yet — fall back to the user's last-used template below.
      }
    }
    const resumeData: ResumeRequest = {
      ...formData,
      templateId: formData.templateId || existingTemplateId || localStorage.getItem(LAST_TEMPLATE_KEY) || '',
      personalInfo: { ...formData.personalInfo, fullName: resolvedName, email: resolvedEmail },
    };
    if (user?.id) {
      try {
        await resumeService.saveProfile(user.id, resumeData);
        trackActivity({ event: 'save_profile', feature: 'manual_create', success: true });
      } catch {
        console.error('Failed to save profile');
        trackActivity({ event: 'save_profile', feature: 'manual_create', success: false });
      }
    }
    navigate('/workspace');
  };

  const handleBackToForm = () => setCurrentStep('form');

  const handleGeneratePdf = async (templateId: string) => {
    const templateInfo = allTemplates.find(t => t.id === templateId);
    if (templateInfo && !templateInfo.free && !isPro) {
      if (subLoading) {
        toast.error('Checking your subscription...');
        return;
      }
      toast.error('This is a Pro template. Upgrade to download it.');
      navigate('/pricing');
      return;
    }
    setIsGenerating(true);
    try {
      const resumeData = { ...formData, templateId };
      const response = await resumeService.generateResume(resumeData);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formData.personalInfo.fullName || 'Resume'}_${templateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Resume downloaded successfully!');
      trackActivity({ event: 'generate_pdf', feature: 'manual_create', success: true, metadata: { template: templateId } });
    } catch (error) {
      console.error('Failed to generate resume:', error);
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 403) {
        toast.error('This is a Pro template. Upgrade to download it.');
        navigate('/pricing');
      } else if (status === 503) {
        toast.error('Payments are temporarily unavailable. You can download once payments are re-enabled and you have an active subscription.');
      } else {
        toast.error(await getBlobErrorMessage(error));
      }
      trackActivity({
        event: 'generate_pdf',
        feature: 'manual_create',
        success: false,
        error: error instanceof Error ? error.message : 'Generation failed',
        metadata: { template: templateId },
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const loadMoreTemplates = async () => {
    if (!templatePage?.hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextOffset = templatePage.offset + templatePage.limit;
      const response = await resumeService.getTemplates(nextOffset, 12);
      if (response.data) {
        const newTemplates = response.data.templates;
        setAllTemplates(prev => [...prev, ...newTemplates]);
        setTemplatePage(response.data);
        const newPreviews: PreviewData[] = newTemplates.map(t => ({ template: t.id, html: '', loading: true }));
        setPreviews(prev => [...prev, ...newPreviews]);
        for (const template of newTemplates) {
          try {
            const previewResp = await resumeService.previewHtml({ ...formData, templateId: template.id });
            setPreviews(prev => prev.map(p => p.template === template.id ? { ...p, html: previewResp.data, loading: false } : p));
          } catch (error) {
            console.error(`Failed to fetch preview for ${template.id}:`, error);
            setPreviews(prev => prev.map(p => p.template === template.id ? { ...p, loading: false } : p));
          }
        }
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const convertToMatchFormat = (data: ResumeRequest): Record<string, unknown> => ({
    personalInfo: data.personalInfo,
    title: data.summary ? `${data.personalInfo.fullName} - ${data.summary.substring(0, 50)}...` : data.personalInfo.fullName,
    summary: data.summary || '',
    experience: data.experience.map(exp => ({
      company: exp.company, role: exp.role, startDate: exp.startDate, endDate: exp.endDate || '',
      currentJob: exp.current, description: exp.description, highlights: [],
    })),
    education: data.education.map(edu => ({
      institution: edu.institution, degree: edu.degree, fieldOfStudy: edu.fieldOfStudy,
      startDate: edu.startDate, endDate: edu.endDate || '', current: false, gpa: edu.gpa || '',
    })),
    skills: data.skills, suggestedSkills: [],
  });

  const handleMatch = async () => {
    if (!useTextMatch && !jdUrl.trim()) return;
    if (useTextMatch && !jdText.trim()) return;
    setIsMatching(true);
    try {
      let result;
      if (useTextMatch) {
        result = await matcherService.matchText(jdText, convertToMatchFormat(formData) as any);
      } else {
        result = await matcherService.match(jdUrl, convertToMatchFormat(formData) as any);
      }
      if (result.success && result.matchedCV && result.jdAnalysis && result.matchScore) {
        setMatchData({
          jdUrl: useTextMatch ? '(pasted text)' : jdUrl,
          matchedCV: result.matchedCV, jdAnalysis: result.jdAnalysis, matchScore: result.matchScore,
        });
        trackActivity({ event: 'match_job', feature: 'manual_create', success: true });
      } else {
        toast.error(result.error || 'Matching failed');
        trackActivity({ event: 'match_job', feature: 'manual_create', success: false, error: result.error || 'Matching failed' });
      }
    } catch (error) {
      console.error('Match error:', error);
      toast.error('Failed to match resume to job');
      trackActivity({
        event: 'match_job',
        feature: 'manual_create',
        success: false,
        error: error instanceof Error ? error.message : 'Match failed',
      });
    } finally {
      setIsMatching(false);
    }
  };

  if (matchData) {
    const handlePdf = async () => {
      setIsGenerating(true);
      try {
        const response = await resumeService.generateResume(formData);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${formData.personalInfo.fullName || 'Resume'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Resume downloaded!');
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 403) {
          toast.error('This is a Pro template. Upgrade to download it.');
          navigate('/pricing');
        } else if (status === 503) {
          toast.error('Payments are temporarily unavailable. You can download once payments are re-enabled and you have an active subscription.');
        } else {
          toast.error(await getBlobErrorMessage(error));
        }
      } finally {
        setIsGenerating(false);
      }
    };

    const handleCoverLetter = async () => {
      try {
        const result = await coverLetterService.generate(
          convertToMatchFormat(formData) as any,
          useTextMatch ? jdText : jdUrl
        );
        if (result.success && result.cover_letter) {
          const blob = new Blob([result.cover_letter], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Cover_Letter_${matchData.jdAnalysis.jobTitle || 'position'}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success('Cover letter downloaded!');
          trackActivity({ event: 'generate_cover_letter', feature: 'manual_create', success: true });
        } else {
          toast.error(result.error || 'Failed to generate cover letter.');
          trackActivity({ event: 'generate_cover_letter', feature: 'manual_create', success: false, error: result.error || 'Failed to generate cover letter' });
        }
      } catch {
        toast.error('Failed to generate cover letter.');
        trackActivity({ event: 'generate_cover_letter', feature: 'manual_create', success: false, error: 'Exception during cover letter generation' });
      }
    };

    return (
      <div className="max-w-[1200px] mx-auto p-8">
        <button
          className="h-10 px-5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2 mb-6"
          onClick={() => setMatchData(null)}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Editor
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-8">
          <div className="space-y-6">
            <MatchScoreComponent score={matchData.matchScore} />
            <JDAnalysisComponent
              analysis={matchData.jdAnalysis}
              keywordMatches={matchData.matchedCV.keywordMatches || []}
            />
          </div>
          <div>
            {matchData.matchedCV.tailoringNotes?.length > 0 && (
              <div className="mb-6 p-5 bg-amber-50 border border-amber-200 rounded-card">
                <h3 className="text-sm font-semibold text-amber-700 mb-3">Tailoring Notes</h3>
                <ul className="space-y-2">
                  {matchData.matchedCV.tailoringNotes.map((note: string, i: number) => (
                    <li key={i} className="text-sm text-amber-600 leading-relaxed flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-3">
              <button
                className="h-10 px-5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                onClick={handlePdf}
                disabled={isGenerating}
              >
                <Download className="w-4 h-4" />
                {isGenerating ? 'Generating...' : 'Download PDF'}
              </button>
              <button
                className="h-10 px-5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                onClick={handleCoverLetter}
              >
                <FileText className="w-4 h-4" />
                Cover Letter
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">Create Resume Manually</h1>
          <p className="text-sm text-zinc-400 mt-1">Loading your profile data...</p>
        </div>
        <FormSkeleton />
      </div>
    );
  }

  if (currentStep === 'form') {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <div className="flex items-center gap-2 text-xs text-zinc-400 mb-3">
            <span className="text-zinc-500">Dashboard</span>
            <span className="text-zinc-300">/</span>
            <span className="text-foreground font-medium">Create Resume</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-1">
            Personal Information
          </h1>
          <p className="text-sm text-zinc-400">
            Your name and email come from your profile. Fill in your other contact details to populate your resume templates.
          </p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-card p-6 sm:p-8 shadow-soft">
          {/* Personal Information */}
          <section className="pb-6 mb-6 border-b border-zinc-100">
            <h3 className="font-display text-base font-semibold text-foreground mb-5">Personal Information</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { label: 'Full Name *', field: 'fullName' as const, placeholder: 'John Doe', readonly: true },
                { label: 'Email *', field: 'email' as const, placeholder: 'john@example.com', type: 'email', readonly: true },
                { label: 'Phone', field: 'phone' as const, placeholder: '+1 555-123-4567' },
                { label: 'Address', field: 'address' as const, placeholder: 'City, State' },
                { label: 'LinkedIn', field: 'linkedin' as const, placeholder: 'https://linkedin.com/in/username' },
                { label: 'Website', field: 'website' as const, placeholder: 'https://yoursite.com' },
              ].map(({ label, field, placeholder, type, readonly }) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-zinc-600 mb-1.5">{label}</label>
                  <input
                    type={type || 'text'}
                    className={`w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-400 transition-all duration-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none ${
                      readonly ? 'bg-zinc-50 text-zinc-500 cursor-not-allowed' : ''
                    }`}
                    placeholder={placeholder}
                    value={readonly ? (field === 'fullName' ? profileFullName : profileEmail) : formData.personalInfo[field] || ''}
                    onChange={readonly ? undefined : e => updatePersonalInfo(field, e.target.value)}
                    readOnly={readonly}
                    title={readonly ? 'Managed in your profile' : undefined}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Professional Summary */}
          <section className="pb-6 mb-6 border-b border-zinc-100">
            <h3 className="font-display text-base font-semibold text-foreground mb-1">Professional Summary</h3>
            <p className="text-xs text-zinc-400 mb-4">Brief overview of your professional background and career objectives.</p>
            <RichTextEditor
              value={formData.summary || ''}
              onChange={updateSummary}
              placeholder="Brief overview of your professional background and career objectives..."
              minHeight="100px"
            />
          </section>

          {/* Experience */}
          <section className="pb-6 mb-6 border-b border-zinc-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-base font-semibold text-foreground">Experience</h3>
              <button onClick={addExperience} className="h-8 px-3 text-xs font-medium text-brand-500 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add Experience
              </button>
            </div>
            {formData.experience.length === 0 && (
              <p className="text-sm text-zinc-400 italic">No experience added yet. Click "Add Experience" to start.</p>
            )}
            {formData.experience.map((exp, index) => (
              <div key={index} className="bg-zinc-50 border border-zinc-100 rounded-btn p-4 mb-3">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-zinc-400">{index + 1}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveExperience(index, -1)}
                      disabled={index === 0}
                      aria-label="Move up"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-btn text-zinc-500 bg-white border border-zinc-200 hover:bg-zinc-100 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveExperience(index, 1)}
                      disabled={index === formData.experience.length - 1}
                      aria-label="Move down"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-btn text-zinc-500 bg-white border border-zinc-200 hover:bg-zinc-100 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Company</label>
                    <input type="text" className="form-input-sm" placeholder="Company name" value={exp.company} onChange={e => updateExperience(index, 'company', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Role / Title</label>
                    <input type="text" className="form-input-sm" placeholder="Job title" value={exp.role} onChange={e => updateExperience(index, 'role', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Start Date</label>
                    <input type="text" className="form-input-sm" placeholder="e.g. Jan 2020" value={exp.startDate} onChange={e => updateExperience(index, 'startDate', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">End Date</label>
                    <input type="text" className="form-input-sm" placeholder="e.g. Dec 2023" value={exp.endDate || ''} onChange={e => updateExperience(index, 'endDate', e.target.value)} disabled={exp.current} />
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-zinc-600 mb-3 cursor-pointer">
                  <input type="checkbox" checked={exp.current} onChange={e => updateExperience(index, 'current', e.target.checked)} className="rounded border-zinc-300 text-brand-500 focus:ring-brand-200" />
                  I currently work here
                </label>
                <RichTextEditor
                  value={exp.description || ''}
                  onChange={value => updateExperience(index, 'description', value)}
                  placeholder="Describe your responsibilities and achievements..."
                  minHeight="60px"
                  className="mt-2"
                />
                <button onClick={() => removeExperience(index)} className="mt-2 h-7 px-2.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-btn transition-colors inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Remove
                </button>
              </div>
            ))}
          </section>

          {/* Education */}
          <section className="pb-6 mb-6 border-b border-zinc-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-base font-semibold text-foreground">Education</h3>
              <button onClick={addEducation} className="h-8 px-3 text-xs font-medium text-brand-500 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add Education
              </button>
            </div>
            {formData.education.length === 0 && (
              <p className="text-sm text-zinc-400 italic">No education added yet. Click "Add Education" to start.</p>
            )}
            {formData.education.map((edu, index) => (
              <div key={index} className="bg-zinc-50 border border-zinc-100 rounded-btn p-4 mb-3">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-zinc-400">{index + 1}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveEducation(index, -1)}
                      disabled={index === 0}
                      aria-label="Move up"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-btn text-zinc-500 bg-white border border-zinc-200 hover:bg-zinc-100 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveEducation(index, 1)}
                      disabled={index === formData.education.length - 1}
                      aria-label="Move down"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-btn text-zinc-500 bg-white border border-zinc-200 hover:bg-zinc-100 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Institution</label>
                    <input type="text" className="form-input-sm" placeholder="University name" value={edu.institution} onChange={e => updateEducation(index, 'institution', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Degree</label>
                    <input type="text" className="form-input-sm" placeholder="e.g. Bachelor of Science" value={edu.degree} onChange={e => updateEducation(index, 'degree', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Field of Study</label>
                    <input type="text" className="form-input-sm" placeholder="e.g. Computer Science" value={edu.fieldOfStudy} onChange={e => updateEducation(index, 'fieldOfStudy', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">GPA</label>
                    <input type="text" className="form-input-sm" placeholder="e.g. 3.8" value={edu.gpa || ''} onChange={e => updateEducation(index, 'gpa', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Start Date</label>
                    <input type="text" className="form-input-sm" placeholder="e.g. Sep 2016" value={edu.startDate || ''} onChange={e => updateEducation(index, 'startDate', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">End Date</label>
                    <input type="text" className="form-input-sm" placeholder="e.g. Jun 2020" value={edu.endDate || ''} onChange={e => updateEducation(index, 'endDate', e.target.value)} />
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
                  <input type="checkbox" checked={edu.current} onChange={e => updateEducation(index, 'current', e.target.checked)} className="rounded border-zinc-300 text-brand-500 focus:ring-brand-200" />
                  Currently enrolled
                </label>
                <button onClick={() => removeEducation(index)} className="mt-3 h-7 px-2.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-btn transition-colors inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Remove
                </button>
              </div>
            ))}
          </section>

          {/* Skills */}
          <section className="pb-2">
            <h3 className="font-display text-base font-semibold text-foreground mb-4">Skills</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                className="form-input-sm flex-1"
                placeholder="Type a skill and press Enter"
                value={skillInput}
                onChange={e => setSkillInput(e.target.value)}
                onKeyDown={handleSkillKeyDown}
              />
              <button onClick={addSkill} className="h-9 px-3 text-xs font-medium text-brand-500 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors">Add</button>
            </div>
            {formData.skills.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {formData.skills.map((skill, index) => (
                  <span key={index} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-zinc-100 text-foreground rounded-full text-xs font-medium">
                    {skill}
                    <button onClick={() => removeSkill(index)} className="text-zinc-400 hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Actions */}
          <div className="flex items-center justify-center gap-3 mt-8 pt-6 border-t border-zinc-100">
            <button onClick={() => navigate('/dashboard')} className="h-10 px-5 text-sm text-zinc-600 hover:text-foreground transition-colors font-medium">
              Cancel
            </button>
            <button onClick={handleContinue} className="h-10 px-5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center gap-2">
              Continue to Preview & Edit <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Templates Step
  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
        <div>
          <button onClick={handleBackToForm} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to Edit
          </button>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-1">
            Choose a Template
          </h1>
          <p className="text-sm text-zinc-400">Select a template to preview and download your resume.</p>
          {templatePage && (
            <p className="text-xs text-zinc-400 mt-1">Showing {allTemplates.length} of {templatePage.total} templates</p>
          )}
        </div>
        <div className="w-full sm:w-80 shrink-0">
          <div className="bg-white border border-zinc-200 rounded-card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground">Match to Job Description</span>
              <div className="flex items-center gap-2">
                <span className={`text-[11px] font-medium ${!useTextMatch ? 'text-brand-500' : 'text-zinc-400'}`}>URL</span>
                <label className="relative inline-block w-8 h-4 cursor-pointer">
                  <input type="checkbox" checked={useTextMatch} onChange={e => setUseTextMatch(e.target.checked)} className="opacity-0 w-0 h-0" />
                  <span className={`absolute inset-0 rounded-full transition-colors duration-200 ${useTextMatch ? 'bg-brand-500' : 'bg-zinc-300'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform duration-200 ${useTextMatch ? 'translate-x-4' : ''}`} />
                  </span>
                </label>
                <span className={`text-[11px] font-medium ${useTextMatch ? 'text-brand-500' : 'text-zinc-400'}`}>Text</span>
              </div>
            </div>
            <div className="flex gap-2">
              {useTextMatch ? (
                <textarea
                  className="flex-1 h-8 px-2 text-xs border border-zinc-200 rounded-btn resize-none bg-white text-foreground placeholder:text-zinc-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  placeholder="Paste job description text..."
                  value={jdText}
                  onChange={e => setJdText(e.target.value)}
                  rows={3}
                />
              ) : (
                <input
                  type="url"
                  className="flex-1 h-8 px-2 text-xs border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  placeholder="Job posting URL..."
                  value={jdUrl}
                  onChange={e => setJdUrl(e.target.value)}
                />
              )}
              <button
                onClick={handleMatch}
                disabled={(useTextMatch ? !jdText.trim() : !jdUrl.trim()) || isMatching}
                className="h-8 px-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1 shrink-0"
              >
                <Search className="w-3 h-3" />
                {isMatching ? '...' : 'Match'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {templatesLoading ? (
        <TemplatesGridSkeleton />
      ) : (
        <>
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate="visible">
            {previews.map(({ template, html, loading }, i) => {
              const templateInfo = allTemplates.find(t => t.id === template);
              const isPremium = templateInfo ? !templateInfo.free : false;
              const locked = isPremium && !isPro;
              return (
              <motion.div
                key={template}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.25 }}
                className="bg-white border border-zinc-200 rounded-card overflow-hidden transition-all duration-300 hover:shadow-elevated hover:-translate-y-0.5 group"
                onMouseEnter={() => setHoveredTemplate(template)}
                onMouseLeave={() => setHoveredTemplate(null)}
              >
                <div className="relative aspect-[3/4] bg-zinc-50 overflow-hidden">
                  {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-zinc-200 border-t-brand-500 rounded-full animate-spin" />
                    </div>
                  ) : (
                    <iframe
                      srcDoc={html}
                      title={template}
                      sandbox="allow-same-origin"
                      className="absolute inset-0 w-[133.33%] h-[133.33%] border-none pointer-events-none origin-top-left scale-[0.75]"
                    />
                  )}
                  {isPremium && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm text-amber-300 text-[10px] font-semibold px-1.5 py-0.5 rounded z-10">
                      <Lock className="w-2.5 h-2.5" /> PRO
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 backdrop-blur-[0px] group-hover:backdrop-blur-[1px] transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewModal(template); }}
                        className="h-8 px-3 bg-white text-foreground text-xs font-medium rounded-btn shadow-soft hover:bg-zinc-50 transition-colors inline-flex items-center gap-1.5"
                      >
                        <Eye className="w-3.5 h-3.5" /> Preview
                      </button>
                      {locked ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleGeneratePdf(template); }}
                          className="h-8 px-3 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1.5"
                        >
                          <Lock className="w-3.5 h-3.5" /> PRO
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleGeneratePdf(template); }}
                          disabled={isGenerating}
                          className="h-8 px-3 bg-brand-500 hover:bg-brand-600 text-white text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                          <Download className="w-3.5 h-3.5" />
                          {isGenerating ? '...' : 'PDF'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground">
                      {templateInfo?.name || template.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </p>
                    {isPremium ? (
                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                        <Lock className="w-2.5 h-2.5" /> PRO
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Free</span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-400">Professional ATS</p>
                </div>
              </motion.div>
              );
            })}
          </motion.div>

          {templatePage?.hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={loadMoreTemplates}
                disabled={loadingMore}
                className="h-10 px-6 border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
              >
                {loadingMore ? (
                  <div className="w-4 h-4 border-2 border-zinc-200 border-t-brand-500 rounded-full animate-spin" />
                ) : (
                  'Load More Templates'
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Preview Modal */}
      <AnimatePresence>
        {previewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[rgba(9,9,11,0.5)] backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
            onClick={() => setPreviewModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="w-full max-w-[1000px] max-h-[85vh] bg-white rounded-card flex flex-col overflow-hidden shadow-elevated"
              onClick={e => e.stopPropagation()}
            >
              <div className="h-16 flex items-center justify-between px-6 border-b border-zinc-200 shrink-0">
                <h2 className="font-display font-semibold text-foreground">
                  {previewModal.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </h2>
                <button
                  onClick={() => setPreviewModal(null)}
                  className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-foreground hover:bg-zinc-100 rounded-btn transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-auto bg-zinc-100 p-6 sm:p-8">
                <div className="max-w-[210mm] mx-auto bg-white shadow-soft aspect-[1/1.414]">
                  <iframe
                    srcDoc={previews.find(t => t.template === previewModal)?.html}
                    title="Full Preview"
                    sandbox="allow-same-origin"
                    className="w-full h-full border-none"
                  />
                </div>
              </div>

              <div className="h-16 flex items-center justify-end gap-3 px-6 border-t border-zinc-200 shrink-0">
                <button
                  onClick={() => setPreviewModal(null)}
                  className="h-9 px-4 text-sm text-zinc-600 hover:text-foreground transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { handleGeneratePdf(previewModal); setPreviewModal(null); }}
                  disabled={isGenerating}
                  className="h-9 px-4 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center gap-2"
                >
                  {isGenerating ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download PDF
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManualCreate;
